import { parseInstant, parseOccurrenceTimingWrite, OCCURRENCE_TIMING_WRITE_FIELDS } from '@liquid-spirit/date-time/validation';
export class SessionRequestContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} request: ${detail}`);
    this.name = 'SessionRequestContractError';
    this.code = 'INVALID_API_REQUEST';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
export const isSessionRequestDate = value => {
  if (value instanceof Date) return Number.isFinite(value.getTime());
  if (typeof value !== 'string' || !value.trim() || !Number.isFinite(Date.parse(value))) return false;
  const calendar = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(value.trim());
  if (!calendar) return true;
  const [, year, month, day] = calendar;
  const date = new Date(`${year}-${month}-${day}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === `${year}-${month}-${day}`;
};
const isTime = value => typeof value === 'string' && /^(?:[01]?\d|2[0-3]):[0-5]\d$/.test(value.trim());
const isObjectId = value => typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value);

export function readSessionIdentifier(value) {
  if (!isObjectId(value)) throw new SessionRequestContractError('identifier', 'expected an ObjectId.');
  return value;
}

export function readSessionListQuery(query = {}) {
  if (!isRecord(query) || query.activityId != null && query.activityId !== '' && !isObjectId(query.activityId)) {
    throw new SessionRequestContractError('list', 'expected a scalar activity ObjectId.');
  }
  return query;
}
const statuses = new Set(['Scheduled', 'Pending', 'Confirmed', 'Completed', 'Cancelled', 'Expired']);
const writeFields = new Set(['date', 'time', 'bookingEndsAt', 'status', 'onlineLink', 'venue', 'venues', 'venueRequest',
  'content', 'details', 'participants', 'facilitators', 'pendingParticipants', 'pendingFacilitators']);
OCCURRENCE_TIMING_WRITE_FIELDS.forEach(field => writeFields.add(field));
const isText = value => typeof value === 'string' && value.trim().length > 0;
export const isSessionRequestPerson = value => {
  if (!isRecord(value)) return false;
  const id = value._id || value.refId;
  if (id) return isObjectId(id);
  return isText(value.firstName) && isObjectId(value.referenceContact)
    && (value.lastName == null || typeof value.lastName === 'string');
};

/** Check the stable create/update body fields before sending a session write. */
export function readSessionWriteRequest(payload, operation = 'session update') {
  if (!isRecord(payload) || (operation === 'session update' && !Object.keys(payload).some(field => writeFields.has(field)))) {
    throw new SessionRequestContractError(operation, 'expected a non-empty object.');
  }
  try { if (payload.bookingEndsAt != null) parseInstant(payload.bookingEndsAt); }
  catch { throw new SessionRequestContractError(operation, 'invalid booking finish timestamp.'); }
  try { parseOccurrenceTimingWrite(payload); } catch (error) { throw new SessionRequestContractError(operation, error.message); }
  if (['timingVersion', 'timingRevision', 'seriesOccurrence', 'reminderPlanRevisions', 'schedulerFence'].some(key => Object.hasOwn(payload, key))) throw new SessionRequestContractError(operation, 'timing and scheduling metadata are server-owned.');
  if (operation === 'session creation' && !isSessionRequestDate(payload.date) && !payload.localStart && !payload.startsAt && !payload.calendarTiming) {
    throw new SessionRequestContractError(operation, 'expected a valid date.');
  }
  if (payload.date !== undefined && !isSessionRequestDate(payload.date)) {
    throw new SessionRequestContractError(operation, 'expected a valid date.');
  }
  if (payload.time != null && payload.time !== '' && !isTime(payload.time)) {
    throw new SessionRequestContractError(operation, 'time must use a valid HH:mm value.');
  }
  if ((payload.status !== undefined && !statuses.has(payload.status))
    || (payload.details != null && !isRecord(payload.details))
    || (payload.curriculumLesson != null && typeof payload.curriculumLesson !== 'string' && !Number.isSafeInteger(payload.curriculumLesson))
    || (payload.onlineLink != null && typeof payload.onlineLink !== 'string')
    || (payload.venue !== undefined && payload.venue !== null && payload.venue !== '' && !isObjectId(payload.venue))
    || (payload.venueRequest != null && payload.venueRequest !== '' && !isObjectId(payload.venueRequest))
    || (payload.venues !== undefined && payload.venues !== null && payload.venues !== ''
      && (Array.isArray(payload.venues)
        ? payload.venues.some(venue => !isObjectId(venue))
        : !isObjectId(payload.venues)))) {
    throw new SessionRequestContractError(operation, 'expected a valid status and venue references.');
  }
  for (const field of ['participants', 'facilitators', 'pendingParticipants', 'pendingFacilitators']) {
    if (payload[field] == null) continue;
    const people = Array.isArray(payload[field]) ? payload[field]
      : operation === 'session update' ? [payload[field]] : null;
    if (!people || people.some(person => !isSessionRequestPerson(person))) {
      throw new SessionRequestContractError(operation, `${field} must contain identified people or guest contacts.`);
    }
  }
  return payload;
}
